import { Chakra } from "@airhelp/react-ui-components";
import BreadcrumbsSchema from "modules/storyblok/components/BreadcrumbsSchema";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import { DataContextProvider } from "contexts/DataContext";
import { useLanguage } from "contexts/LocalizationContext";
import { MobileMenuContextProvider } from "contexts/MobileMenuContext";
import { ModalContextProvider } from "contexts/ModalContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import HeaderNav from "modules/common/components/HeaderNav";
import MenuItems from "modules/common/components/HeaderNav/MenuItems";
import PressLogos from "modules/common/components/PressLogos";
import StickyBottomBarAdapter from "modules/storyblok/adapters/StickyBottomBar";
import { DynamicComponent } from "modules/storyblok/DynamicComponent";
import PropTypes from "prop-types";
import { useTopMenu } from "utils/menuUtils";
import ActionButton from "./ActionButton";

const { Box } = Chakra;

const FlexibleTemplate = ({
  story,
  cmsLanguagesData,
  whiteListDomains,
  serverSideStoryblokPages,
  storyblokComponentData: { trustpilotInfo, reviews },
  settings,
}) => {
  const {
    tag_list: tagList,
    content: {
      metaTitle,
      metaDescription,
      body,
      breadcrumbsParentPage,
      breadcrumbsPositionName,
      stickyBottomBarHeading,
      hideStickyBottomBar,
      stickyBottomBarVariant,
    },
  } = story;
  const language = useLanguage();
  const topMenu = useTopMenu(language);
  const noindex = tagList.includes("noindex");

  return (
    <DataContextProvider
      data={{
        reviews,
        cmsLanguagesData,
        serverSideStoryblokPages,
        trustpilotInfo,
        whiteListDomains,
      }}
    >
      <CustomHead title={metaTitle} description={metaDescription} noindex={noindex} />
      <BreadcrumbsSchema
        breadcrumbsPositionName={breadcrumbsPositionName}
        breadcrumbsParentPage={breadcrumbsParentPage}
      />
      <MobileMenuContextProvider>
        <ModalContextProvider>
          <HeaderNav
            menuItems={<MenuItems menu={topMenu} />}
            headerButtons={<ActionButton activeButtonOption={settings?.activeButtonOption} />}
          />
          <Box pt={24} as="main" sx={{ "#main-page-head": { overflow: "initial" } }}>
            <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
            {body ? body.map((blok) => <DynamicComponent blok={blok} key={blok._uid} />) : null}
          </Box>
          <PressLogos />
          <Footer />
        </ModalContextProvider>
      </MobileMenuContextProvider>
      <StickyBottomBarAdapter
        showStickyBottomBar={!hideStickyBottomBar}
        stickyBottomBarVariant={stickyBottomBarVariant}
        stickyBottomBarHeading={stickyBottomBarHeading}
      />
    </DataContextProvider>
  );
};

const FlexibleTemplateAdapter = (props) => (
  <StickyBottomBarContextProvider threshold={0.4}>
    <FlexibleTemplate {...props} />
  </StickyBottomBarContextProvider>
);

export default FlexibleTemplateAdapter;

FlexibleTemplate.propTypes = {
  story: PropTypes.shape({
    tag_list: PropTypes.arrayOf(PropTypes.string),
    content: PropTypes.shape({
      body: PropTypes.arrayOf(PropTypes.shape({})),
      metaTitle: PropTypes.string,
      metaDescription: PropTypes.string,
      breadcrumbsParentPage: PropTypes.shape({}),
      breadcrumbsPositionName: PropTypes.string,
    }),
  }).isRequired,
  cmsLanguagesData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      slug: PropTypes.string,
      full_slug: PropTypes.string,
      is_folder: PropTypes.bool,
      parent_id: PropTypes.number,
    })
  ),
  settings: PropTypes.shape({
    showStickyBottomBar: PropTypes.bool,
    activeButtonOption: PropTypes.string,
  }).isRequired,
  whiteListDomains: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      dimension_value: PropTypes.string,
    })
  ),
  serverSideStoryblokPages: PropTypes.shape({}).isRequired,
};

FlexibleTemplate.defaultProps = {
  cmsLanguagesData: [],
  whiteListDomains: [],
};
